// src/views/portal/PortalContatoForm.jsx
// Subcomponente do modal de Perfil — permite editar email/celular/WhatsApp
// com confirmação por OTP enviado para o NOVO contato (prova de posse).
import React, { useState } from "react";
import portalService from "../../services/portalService";
import Banner from "../../components/ui/Banner";

const CANAIS = [
  { id: "email",    icone: "bi-envelope-at", label: "E-mail",   placeholder: "novo@email.com" },
  { id: "celular",  icone: "bi-phone",       label: "Celular",  placeholder: "11999990000" },
  { id: "whatsapp", icone: "bi-whatsapp",    label: "WhatsApp", placeholder: "11999990000" },
];

export default function PortalContatoForm({ perfilData, onUpdated }) {
  const [canalAtivo, setCanalAtivo] = useState(null); // qual está em edição
  const [novoValor, setNovoValor]   = useState("");
  const [etapa, setEtapa]           = useState("input"); // input | otp
  const [codigo, setCodigo]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState(null);

  const c = perfilData?.condomino || {};
  const u = perfilData?.usuario || {};

  const atual = {
    email:    u.email,
    celular:  c.celular,
    whatsapp: c.nrWhatsapp,
  };

  const abrirEdicao = (canal) => {
    setCanalAtivo(canal);
    setNovoValor(atual[canal] || "");
    setEtapa("input");
    setCodigo("");
    setMsg(null);
  };

  const cancelar = () => {
    setCanalAtivo(null);
    setNovoValor("");
    setCodigo("");
    setEtapa("input");
    setMsg(null);
  };

  const solicitarOtp = async () => {
    if (!novoValor.trim()) {
      setMsg({ tipo:"warning", texto:"Informe o novo valor." });
      return;
    }
    if (String(atual[canalAtivo] || "").trim() === novoValor.trim()) {
      setMsg({ tipo:"warning", texto:"O novo valor é igual ao atual." });
      return;
    }
    setLoading(true); setMsg(null);
    try {
      const r = await portalService.iniciarMudancaContato(canalAtivo, novoValor.trim());
      setMsg({ tipo: r?.enviado ? "info" : "warning", texto: r?.message });
      setEtapa("otp");
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro ao solicitar código." });
    } finally {
      setLoading(false);
    }
  };

  const confirmarOtp = async () => {
    if (!/^\d{6}$/.test(codigo)) {
      setMsg({ tipo:"warning", texto:"Digite o código de 6 dígitos." });
      return;
    }
    setLoading(true); setMsg(null);
    try {
      const r = await portalService.confirmarMudancaContato(canalAtivo, codigo);
      setMsg({ tipo:"success", texto: r?.message || "Contato atualizado!" });
      onUpdated?.();
      // Pequeno delay para usuário ler a confirmação
      setTimeout(() => cancelar(), 1200);
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro ao confirmar código." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{fontSize:13,color:"var(--gray-500)"}}>
        Para sua segurança, ao alterar um contato você receberá um código de 6 dígitos no <strong>novo</strong> contato informado.
      </div>

      {msg && <Banner tipo={msg.tipo}>{msg.texto}</Banner>}

      {!canalAtivo && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {CANAIS.map(canal => (
            <div key={canal.id}
              style={{
                display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"12px 14px",background:"var(--bg-surface-alt)",borderRadius:10,
                border:"1px solid var(--border-color)",
              }}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <i className={`bi ${canal.icone}`} style={{fontSize:18,color:"#1B3A6B"}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>{canal.label}</div>
                  <div style={{fontSize:12,color:"var(--gray-500)"}}>
                    {atual[canal.id] || <em style={{color:"var(--gray-400)"}}>não informado</em>}
                  </div>
                </div>
              </div>
              <button type="button" className="btn btn-outline-primary btn-sm"
                onClick={()=>abrirEdicao(canal.id)}>
                <i className="bi bi-pencil me-1"/>
                {atual[canal.id] ? "Alterar" : "Cadastrar"}
              </button>
            </div>
          ))}
        </div>
      )}

      {canalAtivo && etapa === "input" && (
        <div style={{display:"flex",flexDirection:"column",gap:10,
          background:"var(--bg-surface-alt)",padding:14,borderRadius:10,border:"1px solid var(--border-color)"}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>
            <i className={`bi ${CANAIS.find(c=>c.id===canalAtivo)?.icone} me-2`}/>
            Alterar {CANAIS.find(c=>c.id===canalAtivo)?.label}
          </div>
          <input
            type={canalAtivo==="email"?"email":"tel"}
            className="form-control"
            value={novoValor}
            onChange={e=>setNovoValor(e.target.value)}
            placeholder={CANAIS.find(c=>c.id===canalAtivo)?.placeholder}
            disabled={loading}
            autoFocus
          />
          <div style={{display:"flex",gap:8}}>
            <button type="button" className="btn btn-primary btn-sm" disabled={loading} onClick={solicitarOtp}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"/>Enviando...</>
                       : <><i className="bi bi-send me-1"/>Enviar código</>}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading} onClick={cancelar}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {canalAtivo && etapa === "otp" && (
        <div style={{display:"flex",flexDirection:"column",gap:10,
          background:"var(--bg-surface-alt)",padding:14,borderRadius:10,border:"1px solid var(--border-color)"}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>
            <i className="bi bi-shield-check me-2"/>
            Digite o código que enviamos
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            className="form-control"
            value={codigo}
            onChange={e=>setCodigo(e.target.value.replace(/\D/g,"").slice(0,6))}
            placeholder="000000"
            disabled={loading}
            autoFocus
            style={{textAlign:"center",fontSize:22,letterSpacing:8,fontWeight:600}}
          />
          <div style={{display:"flex",gap:8}}>
            <button type="button" className="btn btn-success btn-sm" disabled={loading || codigo.length<6} onClick={confirmarOtp}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"/>Confirmando...</>
                       : <><i className="bi bi-check2-circle me-1"/>Confirmar</>}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading} onClick={()=>setEtapa("input")}>
              <i className="bi bi-arrow-left me-1"/>
              Voltar
            </button>
            <button type="button" className="btn btn-link btn-sm" disabled={loading}
              style={{fontSize:12}}
              onClick={solicitarOtp}>
              Reenviar código
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
